import React, { Component } from 'react'
import { observer } from 'mobx-react';
import NavViews from '../stores/NavViews';
import LoginFlow from './LoginFlow';
import { joinGroup, getGroupName } from "../api_calls/user";
import { Icon, Button } from 'utils/ui';


@observer(['CustomRouter', 'UserStore', "ViewStore", "AuthStore"])
class InvitePage extends Component {
    constructor(props) {
        super(props);
        this.isMountedFlag = false;
        this.state = {
            groupName: "",
            inviteId: this.getInviteIdFromRoute(),
            loadingGroupName: true,
            joiningGroup: false,
            joinError: "",
            attemptedJoin: false
        };

        this.handleJoinGroup = this.handleJoinGroup.bind(this);
        this.handleGoHome = this.handleGoHome.bind(this);
    }

    componentDidMount() {
        this.isMountedFlag = true;
        this.loadGroupName();
    }

    componentWillUnmount() {
        this.isMountedFlag = false;
    }

    componentDidUpdate(prevProps, prevState) {
        const currentUserId = this.props.UserStore.user && this.props.UserStore.user._id;
        const previousUserId = prevProps.UserStore.user && prevProps.UserStore.user._id;
        const wasGuest = previousUserId === "guest";
        const isNowAuthed = currentUserId && currentUserId !== "guest";

        if (wasGuest && isNowAuthed && !this.state.attemptedJoin && !this.state.loadingGroupName) {
            this.handleJoinGroup();
        }

        if (
            prevState.loadingGroupName &&
            !this.state.loadingGroupName &&
            isNowAuthed &&
            !this.state.attemptedJoin
        ) {
            this.handleJoinGroup();
        }
    }

    getInviteIdFromRoute() {
        return this.props.CustomRouter.router.params && this.props.CustomRouter.router.params.inviteId;
    }

    async loadGroupName() {
        const inviteId = this.getInviteIdFromRoute();

        if (!inviteId) {
            this.setState({ loadingGroupName: false, joinError: "Invite link is missing or invalid." });
            return;
        }

        try {
            const groupName = await getGroupName(inviteId);
            if (!groupName) {
                this.setState({
                    groupName: "",
                    inviteId,
                    loadingGroupName: false,
                    attemptedJoin: true,
                    joinError: "This invite is invalid or has expired. Ask for a new invite link."
                });
                return;
            }

            this.setState({ groupName, inviteId, loadingGroupName: false });
        } catch (error) {
            const backendError = error && error.response && error.response.data && error.response.data.error;
            this.setState({
                loadingGroupName: false,
                attemptedJoin: true,
                joinError: backendError || "Unable to load invite details. Please try again."
            });
        }
    }

    async handleJoinGroup() {
        const { UserStore, AuthStore } = this.props;
        const userId = UserStore.user && UserStore.user._id;
        const accessToken = AuthStore.userAuth && AuthStore.userAuth.auth && AuthStore.userAuth.auth.accessToken;
        const { inviteId, groupName, joiningGroup } = this.state;

        if (joiningGroup || this.state.loadingGroupName || !inviteId || userId === "guest" || !accessToken) {
            return;
        }

        if (!groupName) {
            this.setState({ joinError: "This invite is invalid or has expired. Ask for a new invite link." });
            return;
        }

        this.setState({ joiningGroup: true, joinError: "", attemptedJoin: true });

        try {
            const res = await joinGroup(userId, accessToken, inviteId, groupName);

            if (res && res.success) {
                await UserStore.refreshGroups(inviteId);
                if (this.isMountedFlag) {
                    this.setState({ joiningGroup: false });
                }
                this.handleGoHome();
                return;
            }

            const backendMessage = res && res.errorMessage;
            if (this.isMountedFlag) {
                this.setState({
                    joiningGroup: false,
                    joinError: backendMessage || "Could not join this group. Please try again."
                });
            }
        } catch (error) {
            console.error("Error joining group:", error);
            const backendError = error && error.response && error.response.data && error.response.data.error;
            if (this.isMountedFlag) {
                this.setState({
                    joiningGroup: false,
                    joinError: backendError || "Could not join this group. Please try again."
                });
            }
        }
    }

    handleGoHome() {
        this.props.CustomRouter.router.goTo(NavViews.home);
    }

    render() {
        const isGuest = this.props.UserStore.user._id === "guest";
        const { loadingGroupName, joiningGroup, joinError } = this.state;

        return (
            <div style={{
                minHeight: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: this.props.UserStore.theme().appBackground || '#a1e0ff',
                padding: '40px 20px'
            }}>
                <div style={{
                    width: '100%',
                    maxWidth: 720,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 8px 24px rgba(20,20,40,0.08)',
                    padding: 28,
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontSize: 20,
                        fontWeight: 600,
                        marginBottom: 8,
                        color: '#222'
                    }}>
                        Welcome to list.gifts!
                    </div>

                    <div style={{
                        fontSize: 16,
                        color: '#444',
                        marginBottom: 20
                    }}>
                        You've been invited to join the{' '}
                        <span style={{ color: '#1890ff', fontWeight: 700 }}>
                            {loadingGroupName ? "group" : (this.state.groupName || 'group')}
                        </span>
                        !
                    </div>

                    {joinError ? (
                        <div style={{ color: '#d93025', marginBottom: 16 }}>
                            {joinError}
                        </div>
                    ) : null}

                    {loadingGroupName ? (
                        <div style={{ color: '#666', marginBottom: 16 }}>
                            <Icon type="loading" style={{ marginRight: 8 }} />
                            Checking invite details...
                        </div>
                    ) : null}

                    {isGuest ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                color: '#666'
                            }}>
                                <Icon type="info-circle" style={{ color: '#1890ff' }} />
                                <span>Sign in or create an account to join this group.</span>
                            </div>

                            <div style={{ width: '100%', maxWidth: 360 }}>
                                <LoginFlow />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Button
                                onClick={this.handleJoinGroup}
                                disabled={joiningGroup || loadingGroupName}
                                style={{
                                    cursor: 'pointer',
                                    background: '#1890ff',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '10px 18px',
                                    borderRadius: 6,
                                    fontSize: 15,
                                    fontWeight: 600,
                                    boxShadow: '0 6px 14px rgba(24,144,255,0.18)',
                                    height: 'auto'
                                }}
                            >
                                <Icon type={joiningGroup ? "loading" : "user-add"} style={{ marginRight: 8, color: '#fff' }} />
                                {joiningGroup ? "Joining Group..." : (joinError ? "Try Again" : "Join Group")}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        )
    }
}
export default InvitePage;
